import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { PlannedMaintenancesService } from "../../../../shared/services/planned-maintenances.service";
import { PlannedMaintenanceDto } from "../../../../shared/models/maintenance/planned-maintenance.dto";
import { AppService } from "../../../../app.service";
import { Agreement } from "../../../../shared/models/agreement/agreement";
import { AgreementsService } from "../../../../shared/services/agreements.service";
import { forkJoin } from "rxjs";
import { AgreementPeriodDto } from "../../../../shared/models/agreement/agreement-period.dto";
import { DialogService } from "@progress/kendo-angular-dialog";
import { NotificationService } from "../../../../core/services/notification.service";
import { Guid } from "guid-typescript";
import { AgreementPriceService } from "../../services/agreement-price-service";
import { SecurityService } from "../../../../core/services/security.service";

@Component({
    selector: 'agreement-planned-maintenances',
    templateUrl: './agreement-planned-maintenances.component.html',
    styleUrls: [
        './agreement-planned-maintenances.component.scss',
        '../../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
        '../../../../../vendor/libs/angular2-ladda/angular2-ladda.scss'
    ]
})
export class AgreementPlannedMaintenancesComponent implements OnInit {

    @Input() agreement: Agreement;
    @Input() devicesMode: boolean = false;
    @Input() isReadonly: boolean = false;

    loading: boolean = false;

    agreementPeriod: AgreementPeriodDto;
    plannedMaintenances: PlannedMaintenanceDto[] = [];
    maintenanceMonths: MaintenanceMonth[] = [];

    selectedMaintenance: PlannedMaintenanceDto;

    constructor(
        private agreementsService: AgreementsService,
        private plannedMaintenancesService: PlannedMaintenancesService,
        private appService: AppService,
        private notificationService: NotificationService,
        private agreementPriceService: AgreementPriceService,
        private changeDetectorRef: ChangeDetectorRef,
        private securityService: SecurityService
    ) { }

    ngOnInit(): void {
        this.loading = true;

        const agreementPeriod$ = this.agreementsService.getAgreementPeriod(this.agreement.agreementId);
        const plannedMaintenances$ = this.plannedMaintenancesService.getAgreementPlannedMaintenances(this.agreement.agreementId);

        forkJoin([agreementPeriod$, plannedMaintenances$]).subscribe(([agreementPeriod, plannedMaintenances]) => {
            this.agreementPeriod = agreementPeriod;
            this.plannedMaintenances = plannedMaintenances;

            this.generateMaintenanceMonths();
            let defaultSelectedIndex = this.maintenanceMonths.findIndex(x => x.plannedMaintenance != null);
            if (defaultSelectedIndex != -1) {
                this.maintenanceMonths[defaultSelectedIndex].selected = true;
                this.selectedMaintenance = this.maintenanceMonths[defaultSelectedIndex].plannedMaintenance;
            }

            this.loading = false;
            this.refreshServicesAndDevicesCount();
            this.changeDetectorRef.detectChanges();
        }, () => this.loading = false);
    }

    updateMaintenance(maintenance: PlannedMaintenanceDto) {
        this.loading = true;
        this.plannedMaintenancesService.update(maintenance, null, true, true).subscribe(() => {
            this.plannedMaintenancesService.getById(maintenance.plannedMaintenanceId).subscribe(resp => {
                let maintenanceIndex = this.maintenanceMonths.findIndex(x => 
                    x.plannedMaintenance != null && x.plannedMaintenance.plannedMaintenanceId == maintenance.plannedMaintenanceId);
                this.maintenanceMonths[maintenanceIndex].plannedMaintenance = resp.entity;
                if (this.selectedMaintenance.plannedMaintenanceId == resp.entity.plannedMaintenanceId) {
                    this.selectedMaintenance = resp.entity;
                }
            });
            this.loading = false;
            this.agreementPriceService.notifyPriceChanged();
            this.refreshServicesAndDevicesCount();
        }, () => this.loading = false);
    }

    generateMaintenanceMonths() {
        this.maintenanceMonths = [];

        this.agreementPeriod.startDate = this.appService.parseDate(this.agreementPeriod.startDate);
        this.agreementPeriod.endDate = this.appService.parseDate(this.agreementPeriod.endDate);
        this.plannedMaintenances.forEach(x => x.plannedDate = this.appService.parseDate(x.plannedDate));
        
        let currentDate = new Date(this.agreementPeriod.startDate);
        let endDate = new Date(this.agreementPeriod.endDate);
        if (currentDate.getDate() != 1) {
            endDate.setDate(endDate.getDate() + 1);
        }

        while (currentDate <= endDate) {
            let maintenanceMonth = new MaintenanceMonth;
            maintenanceMonth.id = Guid.create().toString();
            maintenanceMonth.plannedDate = new Date(currentDate);
            maintenanceMonth.monthNumber = currentDate.getMonth();
            maintenanceMonth.monthName = `${this.appService.getMonthName(currentDate)} ${currentDate.getFullYear()}`;
            maintenanceMonth.plannedMaintenance = this.plannedMaintenances.find(x => 
                x.plannedDate.getMonth() == currentDate.getMonth() && x.plannedDate.getFullYear() == currentDate.getFullYear());

            this.maintenanceMonths.push(maintenanceMonth);
            currentDate.setMonth(currentDate.getMonth() + 1);
        }
    }

    createNewMaintenanceDialog(plannedMonth: MaintenanceMonth) {
        this.notificationService.confirmation({
            title: 'Добавление обслуживания',
            message: `Добавление нового обслуживания на ${plannedMonth.monthName} приведет к перезаписи всех обслуживаний. Продолжить?`,
            confirmButtonText: 'Продолжить',
            cancelButtonText: 'Отмена'
        }, () => this.createNewMaintenance(plannedMonth), () => { });
    }

    createNewMaintenance(plannedMonth: MaintenanceMonth) {
        this.loading = true;
        let newPlannedMaintenance = new PlannedMaintenanceDto;
        newPlannedMaintenance.agreementId = this.agreement.agreementId;
        newPlannedMaintenance.plannedDate = plannedMonth.plannedDate;
        this.plannedMaintenancesService.create(newPlannedMaintenance).subscribe(() => {
            this.plannedMaintenancesService.getAgreementPlannedMaintenances(this.agreement.agreementId).subscribe(resp => {
                this.plannedMaintenances = resp;
                this.generateMaintenanceMonths();
                this.agreementPriceService.notifyPriceChanged();
                this.refreshServicesAndDevicesCount();
                this.loading = false;
            }, () => this.loading = false);
        }, () => this.loading = false);
    }

    deleteMaintenanceDialog(maintenance: PlannedMaintenanceDto) {
        let plannedMonth = this.maintenanceMonths.find(x => 
            x.plannedMaintenance != null && x.plannedMaintenance.plannedMaintenanceId == maintenance.plannedMaintenanceId);

        this.notificationService.confirmation({
            title: 'Удаление обслуживания',
            message: `Удаление обслуживания на ${plannedMonth.monthName} приведет к перезаписи всех обслуживаний. Продолжить?`,
            confirmButtonText: 'Продолжить',
            cancelButtonText: 'Отмена'
        }, () => this.deleteMaintenance(plannedMonth.plannedMaintenance), () => { });
    }

    deleteMaintenance(plannedMaintenance: PlannedMaintenanceDto) {
        this.loading = true;
        this.plannedMaintenancesService.remove(plannedMaintenance.plannedMaintenanceId.toString(), null, null, true).subscribe(() => {
            this.plannedMaintenancesService.getAgreementPlannedMaintenances(this.agreement.agreementId).subscribe(resp => {
                this.plannedMaintenances = resp;
                this.generateMaintenanceMonths();
                this.agreementPriceService.notifyPriceChanged();
                this.refreshServicesAndDevicesCount();
                this.loading = false;
            }, () => this.loading = false);
        }, () => this.loading = false);
    }

    cellClick({ dataItem }) {
        if (dataItem.plannedMaintenance != null) {
            this.selectedMaintenance = dataItem.plannedMaintenance;
            dataItem.selected = true;
            this.maintenanceMonths.filter(x => x.id != dataItem.id).forEach(x => x.selected = false);
        }
       // else {
        //    this.createNewMaintenanceDialog(dataItem);
        //}
    }

    changeDiscountLevel(rate: number) {
        this.agreement.discount = rate;
    }

    refreshServicesAndDevicesCount() {
        let servicesCount = 0;
        let devicesCount = 0;

        this.plannedMaintenances.forEach(maintenance => {
            servicesCount += maintenance.plannedMaintenanceServices.length;
            devicesCount += maintenance.plannedMaintenanceDevices.length;
        });

        this.agreement.plannedServicesCount = servicesCount;
        this.agreement.plannedDevicesCount = devicesCount;
    }

    getSubStatusError(dataItem: MaintenanceMonth): boolean {
        return this.devicesMode && dataItem.plannedMaintenance.plannedMaintenanceDevices.some(x => x.cost == null || x.cost <= 0);
    }

    public rowClass({ dataItem }) {
		return {
			'cursor-pointer': dataItem.plannedMaintenance != null,
            'k-alt': dataItem.plannedMaintenance == null,
            'cell-light-gray': dataItem.selected
		};
	}

    get hasDiscountRight(): boolean {
        return this.securityService.hasClaim('role-Discounting-right');
    }
}

export class MaintenanceMonth {
    id: string;
    monthNumber: number;
    monthName: string;
    plannedMaintenance: PlannedMaintenanceDto;
    selected: boolean;
    plannedDate: Date;
}